// @flow
import { delay } from 'redux-saga';
import { cancel, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import type { Effect } from 'redux-saga';
import { fromJS } from 'immutable';
import { noop } from 'lodash/fp';
import moment from 'moment-timezone';
import { deleteSenseMedia, flagSenseMedia } from '~/core/api/honeycomb';
import { getHubOfflineNotificationsStatus, setHubOfflineNotificationsStatus } from '~/core/api/products/sense/';
import { patchProduct } from '~/core/api/products/products.api';
import { getEvents } from '~/core/api/sensor-events';
import { deviceTypes } from '~/core/constants/devices';
import { alerts as alertConstants, feedback as feedbackConstants } from '~/core/constants/';
import { requestAlert } from '~/core/state/alerts/actions';
import { getTimezone } from '~/core/state/hub/selectors';
import { getTypeForProduct } from '~/core/state/products/selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import * as actions from './actions';
import * as constants from './constants';
import messages from './intl';
import * as types from './types';

export function* setDefaultMutingSaga({
    payload: { productId }
}: {
    payload: { productId: string }
}): Generator<Effect, *, *> {
    const productType = yield select(getTypeForProduct, { id: productId });
    const [, error] = yield call(handleApi(patchProduct), constants.defaultMuting, productType, productId);

    if (error) {
        yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
        return;
    }

    yield put(actions.setDefaultMutingDone({ defaultMuting: constants.defaultMuting, productId }));
}

function* watchSetDefaultMuting(): Generator<Effect, *, *> {
    yield takeLatest(constants.SET_DEFAULT_MUTING, handleSaga(setDefaultMutingSaga));
}

export function* getHubOfflineNotificationsStatusSaga({
    payload: { hubId }
}: types.GetHubOfflineNotificationsStatus): Generator<Effect, *, *> {
    const [status, error] = yield call(handleApi(getHubOfflineNotificationsStatus), hubId);

    if (error) {
        yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
        return;
    }

    yield put(actions.receivedHubOfflineNotificationsStatus(status));
}

function* watchGetHubOfflineNotificationsStatusSaga(): Generator<Effect, *, *> {
    yield takeLatest(
        constants.GET_SENSE_HUB_OFFLINE_NOTICATIONS_STATUS,
        handleSaga(getHubOfflineNotificationsStatusSaga)
    );
}

export function* setHubOfflineNotificationsStatusSaga({
    payload: { hubId, status }
}: types.SetHubOfflineNotificationsStatus): Generator<Effect, *, *> {
    const [, error] = yield call(handleApi(setHubOfflineNotificationsStatus), hubId, status);

    if (error) {
        yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
        return;
    }

    yield put(actions.receivedHubOfflineNotificationsStatus(status));
}

function* watchSetHubOfflineNotificationsStatusSaga(): Generator<Effect, *, *> {
    yield takeLatest(
        constants.SET_SENSE_HUB_OFFLINE_NOTICATIONS_STATUS,
        handleSaga(setHubOfflineNotificationsStatusSaga)
    );
}

export function* toggleMonitoringAllSaga({
    payload: { isMonitoring, productId }
}: types.ToggleMonitoringAllAction): Generator<Effect, *, *> {
    // mute = (true) || (false) =  (inactive/notMonitoring) || (active/monitoring)
    const mute = isMonitoring;
    const productType = yield select(getTypeForProduct, { id: productId });
    const [, error] = yield call(handleApi(patchProduct), { mute }, productType, productId);

    if (error) {
        yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
        return;
    }

    yield put(actions.toggleMonitoringAllDone({ isMonitoring, productId }));
}

function* watchToggleMonitoringAllSaga(): Generator<Effect, *, *> {
    yield takeEvery(constants.TOGGLE_MONITORING_ALL, handleSaga(toggleMonitoringAllSaga));
}

export function* toggleMonitoringSensorSaga({
    payload: { isMonitoring, productId, sensorType }
}: types.ToggleMonitoringSensorAction): Generator<Effect, *, *> {
    const productType = yield select(getTypeForProduct, { id: productId });
    const [, error] = yield call(
        handleApi(patchProduct),
        { sensors: { [sensorType]: isMonitoring } },
        productType,
        productId
    );

    if (error) {
        yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
        return;
    }

    yield put(actions.toggleMonitoringSensorDone({ productId, sensorType, isMonitoring }));
}

function* watchToggleMonitoringSensorSaga(): Generator<Effect, *, *> {
    yield takeEvery(constants.TOGGLE_MONITORING_SENSOR, handleSaga(toggleMonitoringSensorSaga));
}

function* eventPollingDaemonSaga(action): Generator<Effect, *, *> {
    yield call(getSenseEventLogs, action);
    yield call(delay, 30000);
    yield call(eventPollingDaemonSaga, action);
}

export function* watchEventPollingDaemonSaga(): Generator<Effect, *, *> {
    while (true) {
        const daemon = yield takeLatest(constants.CHANGE_SENSE_LOGS_SELECTED_DAY, eventPollingDaemonSaga);
        yield take(constants.STOP_SENSE_DAY_EVENT_LOGS_POLLING);
        yield cancel(daemon);
    }
}

export function* getSenseEventLogs({ payload: { id, selectedDay } }: types.IdDay): Generator<Effect | noop, *, *> {
    const timezone = yield select(getTimezone);
    const startOfDay = moment.tz(selectedDay, timezone).startOf('day');
    const from = startOfDay.valueOf();
    const to = moment
        .tz(selectedDay, timezone)
        .endOf('day')
        .valueOf();

    yield put(actions.getSenseDayEventLogs(id, selectedDay));

    const [response, error] = yield call(handleApi(getEvents), deviceTypes.SENSE, id, from, to);

    if (error) {
        yield put(requestAlert(messages.getEventsError, alertConstants.types.ERROR));
        return yield put(actions.receiveSenseDayEventLogs(error));
    }

    yield put(actions.receiveSenseDayEventLogs(id, startOfDay.format('YYYY-MM-DD'), fromJS(response)));
}

export function* flagSenseMediaSaga({
    payload: { id, mediaId, flag }
}: types.FlagSenseMediaAction): Generator<Effect | noop, *, *> {
    const [, error] = yield call(handleApi(flagSenseMedia), mediaId, flag);

    if (error) {
        yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
        return;
    }

    yield put(actions.flagSenseMediaComplete(id, mediaId));
}

export function* watchChangeFlagSenseMediaSaga(): Generator<Effect, *, *> {
    yield takeEvery(constants.FLAG_SENSE_MEDIA, handleSaga(flagSenseMediaSaga));
}

export function* deleteSenseEventSaga({
    payload: { id, mediaId }
}: types.DeleteSenseEvent): Generator<Effect | noop, *, *> {
    const [, error] = yield call(handleApi(deleteSenseMedia), mediaId);

    if (error) {
        yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
        return;
    }

    yield put(actions.deleteSenseEventComplete(id, mediaId));
}

export function* watchDeleteSenseEventSaga(): Generator<Effect, *, *> {
    yield takeEvery(constants.DELETE_SENSE_EVENT, handleSaga(deleteSenseEventSaga));
}

export default [
    watchChangeFlagSenseMediaSaga,
    watchDeleteSenseEventSaga,
    watchEventPollingDaemonSaga,
    watchSetDefaultMuting,
    watchGetHubOfflineNotificationsStatusSaga,
    watchSetHubOfflineNotificationsStatusSaga,
    watchToggleMonitoringAllSaga,
    watchToggleMonitoringSensorSaga
];



// WEBPACK FOOTER //
// ./src/core/state/products/sense/sagas.js